import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useAssistant } from './AssistantProvider';
import Header from './Header';
import Transcript from './Transcript';
import Composer from './Composer';
import ConversationList from './ConversationList';
import { CheckIcon, CloseIcon, WarnIcon } from './icons';
import { MAX_MESSAGE_LENGTH, MAX_SELECTED_OPTIONS } from './errors';
import type { AssistantErrorCode } from './errors';

const SMALL_BREAKPOINT = 560;

function errorText(code: AssistantErrorCode, t: TFunction): string {
  switch (code) {
    case 'rate_limited':
      return t('Too many messages. Please wait a moment and try again.');
    case 'invalid_message':
      return t(
        'This message could not be sent. Messages can be at most {{max}} characters.',
        { max: MAX_MESSAGE_LENGTH }
      );
    case 'invalid_answer':
      return t('Your answer could not be submitted. Please try again.');
    case 'action_conflict':
      return t('This action has expired or was already decided.');
    case 'question_conflict':
      return t('This question was already answered.');
    case 'history_failed':
      return t('Could not load this conversation.');
    case 'list_failed':
      return t('Could not load your conversations.');
    case 'too_many_options':
      return t('You can select at most {{max}} options.', {
        max: MAX_SELECTED_OPTIONS,
      });
    default:
      return t('Something went wrong. Please try again.');
  }
}

/* Docked right-side panel. Slides in when open; switches between chat + list
 * views. Resizable via a left-edge drag handle; full-screen and pinned modes
 * come from the provider (pinned reserves width in the layout shell). */
const Panel = () => {
  const { t } = useTranslation('assistant');
  const {
    open,
    listView,
    reconnecting,
    banner,
    fullScreen,
    width,
    setWidth,
    resizing,
    setResizing,
    error,
    dismissError,
  } = useAssistant();

  const [small, setSmall] = useState(false);
  const draggingRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Closed panel stays mounted (CSS moves it offscreen) — make it inert so
  // its buttons/composer aren't keyboard/screen-reader reachable while hidden,
  // and hand focus back to the launcher button that reopens it.
  const wasOpen = useRef(open);
  useEffect(() => {
    const el = panelRef.current;
    if (el) el.toggleAttribute('inert', !open);
    if (wasOpen.current && !open) {
      document.querySelector<HTMLButtonElement>('.bra-fab')?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // Suppress the slide animation on the very first render if restored open.
  const [instant, setInstant] = useState(open);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      const id = requestAnimationFrame(() => setInstant(false));
      return () => cancelAnimationFrame(id);
    }
  }, []);

  // Track the small breakpoint (panel is full-width there; handle hidden).
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${SMALL_BREAKPOINT - 1}px)`);
    const onChange = () => setSmall(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      draggingRef.current = true;
      setResizing(true);
    },
    [setResizing]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return;
      setWidth(window.innerWidth - e.clientX); // provider clamps (overlay/pinned)
    },
    [setWidth]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* noop */
      }
      draggingRef.current = false;
      setResizing(false); // provider persists the width when resizing ends
    },
    [setResizing]
  );

  const panelStyle =
    fullScreen || small ? { width: '100vw' } : { width: `${width}px` };
  const showHandle = !fullScreen && !small;

  return (
    <div
      ref={panelRef}
      className={
        `bra-panel${open ? ' bra-open' : ''}` +
        `${instant ? ' bra-instant' : ''}` +
        `${resizing ? ' bra-resizing' : ''}` +
        `${fullScreen ? ' bra-fs' : ''}`
      }
      style={panelStyle}
    >
      {showHandle ? (
        <div
          className="bra-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('Drag to resize')}
          title={t('Drag to resize')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      ) : null}

      <Header />

      {reconnecting ? (
        <div className="bra-banner bra-warn">
          <span className="bra-spin" />
          {t('Connection lost — reconnecting…')}
        </div>
      ) : banner?.kind === 'ok' ? (
        <div className="bra-banner bra-ok">
          <CheckIcon className="bra-ok" width={13} height={13} />
          {t('Reconnected — refetched {{count}} missed message', {
            count: Number(banner.text) || 0,
          })}
        </div>
      ) : null}

      {error ? (
        <div className="bra-banner bra-err" role="alert">
          <WarnIcon width={13} height={13} />
          <span className="bra-err-text">{errorText(error, t)}</span>
          <button
            type="button"
            className="bra-err-close"
            aria-label={t('Dismiss')}
            onClick={dismissError}
          >
            <CloseIcon width={12} height={12} />
          </button>
        </div>
      ) : null}

      {listView ? (
        <ConversationList />
      ) : (
        <div className="bra-chatview">
          <Transcript />
          <Composer />
        </div>
      )}
    </div>
  );
};

export default Panel;
